/**
 * meta/prize-desk.js — the redemption counter at the end of the midway (GDD 7.5).
 *
 * The hub already places the desk furniture; this adds everything that makes it
 * a place you can do something at: the barker goose who works it, an apron
 * sign, and two interactive panels — prize sets on the left, wardrobe on the
 * right — that a VR player points at and a desktop player reads while the DOM
 * overlay handles the clicking.
 *
 * The sets panel is two-level: the top level lists the eight sets, selecting
 * one drills into its pieces, and a back row returns. That keeps a readable row
 * height on a panel the player is standing two metres from, rather than trying
 * to fit fifty-three pieces onto one board.
 *
 * @module meta/prize-desk
 */

import * as THREE from 'three';
import { HUB, META_INTERACT_RADIUS, EVENTS, META_EVENTS } from '../constants.js';
import { bus } from '../core/events.js';
import { teleport } from '../core/teleport.js';
import { economy } from '../core/save.js';
import { logCall } from '../core/testmode.js';
import * as factory from '../assets3d/factory.js';
import { hub } from '../world/hub.js';
import { createButtonPanel } from './panel-ui.js';
import { sets } from './prize-sets.js';
import { cosmetics } from './cosmetics-catalog.js';

/** Where the player stands to use the desk — 2.2 m out, matching the teleport node. */
const INTERACT_POINT = new THREE.Vector3(HUB.prizeCounter.x, 0, HUB.prizeCounter.z + 2.2);

class PrizeDesk {
  constructor() {
    /** @type {THREE.Group|null} */ this.root = null;
    /** @type {THREE.Mesh|null} */ this.setsPanel = null;
    /** @type {THREE.Mesh|null} */ this.wardrobePanel = null;
    /** @type {THREE.Group|null} */ this.barker = null;
    this._open = false;
    this._near = false;
    /** Which set the sets panel has drilled into, or null at the top level. */
    this._openSet = null;
    this._elapsed = 0;
  }

  /**
   * @param {THREE.Scene} scene
   * @returns {this}
   */
  build(scene) {
    if (this.root) return this;

    this.root = new THREE.Group();
    this.root.name = 'meta-prize-desk';
    scene.add(this.root);

    this._orientCounter();
    this._buildBarker();
    this._buildPanels();
    this.refresh();
    return this;
  }

  /**
   * Point the counter and its visitor at each other.
   *
   * As the hub places it, the desk opens toward −Z while the midway approaches
   * from +Z, and the `prize-counter` teleport node faces +Z — back up the
   * midway, away from the desk. A player arriving there therefore looks at
   * nothing, and because the third-person camera sits 3.6 m *behind* the goose,
   * it lands inside the desk's back wall and fills the screen with the backs of
   * the prize shelves. Two corrections fix both halves:
   *
   *   desk  → turn 180° so its open face and shelves greet the midway
   *   node  → arrive facing −Z, which also puts the follow camera out in the
   *           open on the approach side
   *
   * Applied here at runtime rather than edited into `hub.js`, which several
   * agents are working in concurrently.
   * @private
   */
  _orientCounter() {
    const counter = hub.root?.getObjectByName('prize-counter');
    if (counter) counter.rotation.y = Math.PI;

    const node = teleport.getNode('prize-counter');
    if (node) node.yaw = Math.PI;
  }

  /** @private The barker goose (GDD 7.5) — beside the counter, greeting arrivals. */
  _buildBarker() {
    const barker = factory.createGoose({ color: 0xf6efe2 });
    barker.name = 'prize-barker';
    // Beside the desk rather than squeezed between counter and back wall, and
    // turned to face the midway (the goose model faces −Z by default).
    barker.position.set(HUB.prizeCounter.x + 2.5, 0, HUB.prizeCounter.z + 0.5);
    barker.rotation.y = Math.PI;
    this.root.add(barker);
    this.barker = barker;

    const apron = factory.createSign('', {
      w: 0.52, h: 0.3,
      lines: ['PRIZES'],
      bg: '#9e2b3a',
      color: '#ffd166',
      size: 46,
    });
    apron.position.set(0, 0.46, -0.31);
    apron.rotation.y = Math.PI;
    barker.add(apron);

    const hat = factory.createSign('', {
      w: 1.5, h: 0.42,
      lines: ['REDEEM YOUR TOKENS'],
      bg: '#1b2340',
      size: 40,
    });
    hat.position.set(HUB.prizeCounter.x, 2.05, HUB.prizeCounter.z + 1.05);
    this.root.add(hat);
  }

  /** @private Two panels standing on the counter, facing back up the midway. */
  _buildPanels() {
    // A PlaneGeometry faces +Z, which is straight at an arriving player.
    this.setsPanel = createButtonPanel({
      width: 1.85, height: 1.35,
      title: 'PRIZE SETS',
      subtitle: 'Point and pull the trigger',
    });
    this.setsPanel.position.set(HUB.prizeCounter.x - 1.05, 1.78, HUB.prizeCounter.z + 1.15);
    this.setsPanel.rotation.y = 0.12;
    this.root.add(this.setsPanel);
    this.setsPanel.onSelect((id) => this._onSetsSelect(id));

    this.wardrobePanel = createButtonPanel({
      width: 1.5, height: 1.35,
      title: 'WARDROBE',
      subtitle: 'Wear what you have earned',
    });
    this.wardrobePanel.position.set(HUB.prizeCounter.x + 1.0, 1.78, HUB.prizeCounter.z + 1.15);
    this.wardrobePanel.rotation.y = -0.12;
    this.root.add(this.wardrobePanel);
    this.wardrobePanel.onSelect((id) => this._onWardrobeSelect(id));
  }

  // ── Content ─────────────────────────────────────────────────────────────

  /** Redraw both panels from live save state. */
  refresh() {
    if (this.setsPanel) this._drawSets();
    if (this.wardrobePanel) this._drawWardrobe();
  }

  /** @private */
  _drawSets() {
    const tokens = economy.tokens();

    if (!this._openSet) {
      const rows = sets.all().map((s) => ({
        id: `set:${s.id}`,
        label: s.name,
        right: s.complete ? '✓' : `${s.owned}/${s.total}`,
        state: s.complete ? 'owned' : (s.available ? 'normal' : 'disabled'),
        note: s.available
          ? (s.complete ? s.cosmeticName : `unlocks ${s.cosmeticName}`)
          : `${s.lockLabel} · ${s.returnsInDays}d`,
      }));
      this.setsPanel.setContent({
        title: 'PRIZE SETS',
        subtitle: `${tokens} tokens · season ${sets.season()}`,
        rows,
        footer: 'Select a set to see its pieces',
      });
      return;
    }

    const set = sets.all().find((s) => s.id === this._openSet);
    if (!set) { this._openSet = null; this._drawSets(); return; }

    const rows = [{ id: 'back', label: '‹ All sets', state: 'normal' }];
    for (const p of set.pieceList) {
      const affordable = set.available && !p.owned && tokens >= p.cost;
      rows.push({
        id: p.owned ? null : `buy:${p.id}`,
        label: p.name,
        right: p.owned ? 'OWNED' : `${p.cost}`,
        state: p.owned ? 'owned' : (affordable ? 'normal' : 'disabled'),
      });
    }

    this.setsPanel.setContent({
      title: set.name.toUpperCase(),
      subtitle: `${set.owned}/${set.total} · ${tokens} tokens`,
      rows,
      footer: set.complete
        ? `${set.cosmeticName} unlocked`
        : set.available ? `Complete for ${set.cosmeticName}` : set.lockLabel,
    });
  }

  /** @private */
  _drawWardrobe() {
    const owned = cosmetics.catalog().filter((c) => c.owned);
    const rows = owned.length
      ? owned.map((c) => ({
        id: `equip:${c.slot}:${c.id}`,
        label: c.name,
        right: c.equipped ? 'ON' : '',
        state: c.equipped ? 'selected' : 'normal',
        note: `${c.slot} slot`,
      }))
      : [{ label: 'Nothing unlocked yet', state: 'disabled' }];

    this.wardrobePanel.setContent({
      title: 'WARDROBE',
      subtitle: `${owned.length} unlocked`,
      rows,
      footer: owned.length ? 'Select to wear or remove' : 'Complete a prize set',
    });
  }

  // ── Selection ───────────────────────────────────────────────────────────

  /** @private @param {string} id */
  _onSetsSelect(id) {
    if (id === 'back') {
      this._openSet = null;
    } else if (id.startsWith('set:')) {
      this._openSet = id.slice(4);
    } else if (id.startsWith('buy:')) {
      const result = sets.purchase(id.slice(4));
      if (!result.ok) {
        bus.emit(EVENTS.UI_TOAST, { text: reasonText(result.reason), kind: 'bad', duration: 2400 });
      }
    }
    this.refresh();
  }

  /** @private @param {string} id */
  _onWardrobeSelect(id) {
    if (!id.startsWith('equip:')) return;
    const [, slot, cosmeticId] = id.split(':');
    const isOn = cosmetics.equipped()[slot] === cosmeticId;
    cosmetics.equip(slot, isOn ? null : cosmeticId);
    this.refresh();
  }

  // ── Presence ────────────────────────────────────────────────────────────

  /**
   * @param {number} dt
   * @param {THREE.Vector3} playerPos
   */
  update(dt, playerPos) {
    if (!this.root) return;
    this._elapsed += dt;

    // A gentle idle so the barker is not a statue.
    if (this.barker) {
      this.barker.position.y = Math.abs(Math.sin(this._elapsed * 1.6)) * 0.03;
      this.barker.rotation.y = Math.PI + Math.sin(this._elapsed * 0.6) * 0.12;
    }

    if (!playerPos) return;
    const near = playerPos.distanceTo(INTERACT_POINT) <= META_INTERACT_RADIUS;
    if (near === this._near) return;
    this._near = near;

    bus.emit(EVENTS.UI_PROMPT, {
      text: near ? 'Prize Desk — press F to browse' : '',
      show: near,
    });
    if (near) this.refresh();
  }

  /** @returns {boolean} Is the player standing at the desk? */
  isNear() {
    return this._near;
  }

  /** @returns {THREE.Vector3} Where the player stands to use the desk. */
  interactPoint() {
    return INTERACT_POINT.clone();
  }

  // ── Stub-compatible surface (ARCHITECTURE §10) ──────────────────────────

  open() {
    logCall('meta', 'prizeDesk.open', []);
    this._open = true;
    this.refresh();
    bus.emit(META_EVENTS.DESK_OPEN, {});
  }

  close() {
    logCall('meta', 'prizeDesk.close', []);
    this._open = false;
    bus.emit(META_EVENTS.DESK_CLOSE, {});
  }

  /** @returns {boolean} */
  isOpen() {
    return this._open;
  }

  /** @param {string} pieceId @returns {boolean} */
  canAfford(pieceId) {
    return sets.canAfford(pieceId);
  }

  /** @param {string} pieceId @returns {{ok: boolean, reason?: string}} */
  purchase(pieceId) {
    logCall('meta', 'prizeDesk.purchase', [pieceId]);
    const result = sets.purchase(pieceId);
    this.refresh();
    return result;
  }

  /** @returns {Array<object>} Every piece across every set. */
  catalog() {
    return sets.catalog();
  }

  /**
   * Drill the in-world panel into a set (or back out with `null`).
   * @param {string|null} setId
   */
  showSet(setId) {
    this._openSet = setId;
    this.refresh();
    return this._openSet;
  }

  /** @returns {string|null} */
  openSet() {
    return this._openSet;
  }
}

/** @param {string} [reason] @returns {string} */
function reasonText(reason) {
  switch (reason) {
    case 'insufficient-tokens': return 'Not enough tokens';
    case 'out-of-season': return 'That set is out of season';
    case 'already-owned': return 'You already have that piece';
    default: return 'Cannot buy that';
  }
}

/** The one prize desk. */
export const prizeDesk = new PrizeDesk();
export default prizeDesk;
